"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateState = validateState;
const Types_1 = require("./Types");
const Rules_1 = require("./Rules");
function requireState(condition, message) {
    if (!condition)
        throw new Error(`Invalid Euchre state: ${message}`);
}
function counts(value) {
    return Array.isArray(value) && value.length === 2
        && [0, 1].every(index => Number.isSafeInteger(value[index]) && value[index] >= 0);
}
function validateState(state, config) {
    requireState(state && typeof state === "object", "expected a snapshot object");
    requireState(state.config && state.config.seed === config.seed
        && (state.config.dealerIndex ?? 0) === (config.dealerIndex ?? 0), "configuration does not match the engine");
    requireState(Object.values(Types_1.Phase).includes(state.phase), "unknown phase");
    requireState(Number.isSafeInteger(state.handNumber) && state.handNumber >= 0, "invalid hand number");
    requireState(Types_1.PLAYERS.includes(state.dealerIndex) && Types_1.PLAYERS.includes(state.currentPlayerIndex), "invalid player index");
    requireState(state.biddingRound === 1 || state.biddingRound === 2, "invalid bidding round");
    requireState(Number.isInteger(state.passes) && state.passes >= 0 && state.passes <= 3, "invalid pass count");
    requireState(Array.isArray(state.hands) && state.hands.length === 4
        && Types_1.PLAYERS.every(player => Array.isArray(state.hands[player])), "expected four hands");
    requireState(Array.isArray(state.kitty) && Array.isArray(state.currentTrick) && Array.isArray(state.completedTricks), "missing card piles");
    requireState(counts(state.scores) && counts(state.tricks), "invalid scores or trick counts");
    requireState(state.completedTricks.length <= 5 && state.tricks[0] + state.tricks[1] === state.completedTricks.length, "trick counts do not match completed tricks");
    requireState(state.trump === null || Types_1.SUITS.includes(state.trump), "invalid trump");
    requireState(state.makerIndex === null || Types_1.PLAYERS.includes(state.makerIndex), "invalid maker");
    requireState(typeof state.alone === "boolean", "invalid alone flag");
    requireState(state.inactivePlayerIndex === (state.alone && state.makerIndex !== null ? (0, Rules_1.partnerOf)(state.makerIndex) : null), "inactive player does not match the lone maker");
    requireState(state.winningTeam === null || state.winningTeam === 0 || state.winningTeam === 1, "invalid winning team");
    const deck = (0, Rules_1.createDeck)();
    const checkCard = (card) => {
        requireState(card && typeof card === "object", "invalid card");
        const canonical = deck[card.id];
        requireState(canonical && canonical.id === card.id && canonical.suit === card.suit && canonical.rank === card.rank, "card ID, suit and rank must match the Euchre deck");
    };
    const checkPlays = (plays) => {
        requireState(Array.isArray(plays), "invalid trick plays");
        for (const play of plays) {
            requireState(play && Types_1.PLAYERS.includes(play.playerIndex), "invalid trick player");
            checkCard(play.card);
        }
        requireState(new Set(plays.map(play => play.playerIndex)).size === plays.length, "player repeated in a trick");
    };
    checkPlays(state.currentTrick);
    for (const trick of state.completedTricks) {
        requireState(trick && Types_1.PLAYERS.includes(trick.winnerIndex), "invalid trick winner");
        checkPlays(trick.plays);
    }
    const cards = [
        ...state.hands.flat(), ...state.kitty, ...state.currentTrick.map(play => play.card),
        ...state.completedTricks.flatMap(trick => trick.plays.map(play => play.card))
    ];
    cards.forEach(checkCard);
    requireState(new Set(cards.map(card => card.id)).size === cards.length, "duplicate card");
    if (state.upcard !== null)
        checkCard(state.upcard);
    if (state.phase === Types_1.Phase.READY) {
        requireState(state.handNumber === 0 && cards.length === 0 && state.upcard === null
            && state.hands.every(hand => hand.length === 0) && state.kitty.length === 0
            && state.trump === null && state.makerIndex === null && !state.alone
            && state.currentTrick.length === 0 && state.completedTricks.length === 0
            && state.tricks.every(value => value === 0) && state.dealerIndex === (config.dealerIndex ?? 0)
            && state.biddingRound === 1 && state.passes === 0 && state.currentPlayerIndex === state.dealerIndex
            && state.lastResult === null && state.winningTeam === null && state.scores.every(score => score === 0), "inconsistent ready phase");
        return;
    }
    requireState(state.handNumber >= 1 && cards.length === 24 && state.upcard !== null, "a dealt hand must contain all 24 cards and an upcard");
    const ended = state.phase === Types_1.Phase.HAND_OVER || state.phase === Types_1.Phase.GAME_OVER;
    requireState(ended || (state.lastResult === null && state.winningTeam === null), "unfinished hand has a result");
    requireState(state.phase === Types_1.Phase.GAME_OVER || state.scores.every(score => score < 10), "match already has a winner");
    if (state.phase === Types_1.Phase.BIDDING) {
        let expected = (0, Rules_1.nextPlayer)(state.dealerIndex);
        for (let pass = 0; pass < state.passes; pass++)
            expected = (0, Rules_1.nextPlayer)(expected);
        requireState(state.trump === null && state.makerIndex === null && !state.alone
            && state.currentTrick.length === 0 && state.completedTricks.length === 0
            && state.hands.every(hand => hand.length === 5) && state.kitty.length === 4
            && state.kitty[0].id === state.upcard.id && state.currentPlayerIndex === expected, "inconsistent bidding phase");
        return;
    }
    requireState(state.trump !== null && state.makerIndex !== null, "trump and maker are required after bidding");
    if (state.phase === Types_1.Phase.DISCARD) {
        requireState(state.biddingRound === 1 && state.currentPlayerIndex === state.dealerIndex
            && state.trump === state.upcard.suit && state.kitty.length === 3
            && state.currentTrick.length === 0 && state.completedTricks.length === 0
            && state.hands.every((hand, player) => hand.length === (player === state.dealerIndex ? 6 : 5))
            && state.hands[state.dealerIndex].some(card => card.id === state.upcard.id), "inconsistent discard phase");
        return;
    }
    requireState(state.kitty.length === 4, "play requires a four-card kitty");
    const activeCount = state.alone ? 3 : 4;
    const nextActive = (player) => {
        const next = (0, Rules_1.nextPlayer)(player);
        return next === state.inactivePlayerIndex ? (0, Rules_1.nextPlayer)(next) : next;
    };
    const remaining = [5, 5, 5, 5];
    const tricks = [0, 0];
    let leader = nextActive(state.dealerIndex);
    const checkOrder = (plays) => {
        let expected = leader;
        for (const play of plays) {
            requireState(play.playerIndex === expected, "incorrect trick play order");
            remaining[play.playerIndex]--;
            expected = nextActive(expected);
        }
    };
    for (const trick of state.completedTricks) {
        requireState(trick.plays.length === activeCount, "incomplete completed trick");
        checkOrder(trick.plays);
        requireState((0, Rules_1.trickWinner)(trick.plays, state.trump) === trick.winnerIndex, "incorrect trick winner");
        tricks[(0, Rules_1.teamOf)(trick.winnerIndex)]++;
        leader = trick.winnerIndex;
    }
    requireState(tricks.every((value, team) => value === state.tricks[team]), "incorrect team trick counts");
    requireState(state.currentTrick.length < activeCount, "current trick is already complete");
    checkOrder(state.currentTrick);
    requireState(state.hands.every((hand, player) => hand.length === remaining[player]), "hand sizes do not match played cards");
    const lastPlay = state.currentTrick.at(-1);
    requireState(state.currentPlayerIndex === (lastPlay ? nextActive(lastPlay.playerIndex) : leader), "incorrect current turn");
    if (state.phase === Types_1.Phase.PLAYING) {
        requireState(state.completedTricks.length < 5, "playing phase already has five tricks");
        return;
    }
    requireState(state.completedTricks.length === 5 && state.currentTrick.length === 0, "completed hand needs five tricks");
    const result = (0, Rules_1.scoreHand)(state.makerIndex, state.tricks, state.alone);
    requireState(state.lastResult && state.lastResult.makerIndex === result.makerIndex
        && state.lastResult.makerTeam === result.makerTeam && state.lastResult.winningTeam === result.winningTeam
        && state.lastResult.points === result.points && state.lastResult.alone === result.alone
        && state.lastResult.euchred === result.euchred && counts(state.lastResult.tricks)
        && state.lastResult.tricks.every((value, team) => value === result.tricks[team]), "incorrect hand result");
    requireState(state.scores[result.winningTeam] >= result.points, "score does not include the hand result");
    requireState(state.phase === Types_1.Phase.GAME_OVER
        ? state.winningTeam === result.winningTeam && state.scores[result.winningTeam] >= 10
            && state.scores[result.winningTeam === 0 ? 1 : 0] < 10
        : state.winningTeam === null, "incorrect match result");
}
